import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getLoggedUserId,
  getLoginData,
  getMyTeams,
  getOrgUnitData,
  hasAnyHrRelatedRole,
  hasManagerRole,
  isOnlyAltMgr,
} from '@/old/utils/helper';
import Service from '@/app/utils/service';
import API from '@/app/api/internalAPIs';
import {
  CommonWorkflowReportScopeSettings,
  CommonWorkflowReportUnitData,
} from '@/app/components/WorkflowsReport/ReportTypes/Common';
import {
  ReportToolbarSettingsChange,
  ReportToolbarSettingsChangeAction,
  WorkflowsReportToolbarSettings,
} from '@/app/components/WorkflowsReport/components/ToolbarManagerAndHr';
import { useWorklistReportSettings } from '@/app/components/Reporting/Collaboration/WorklistsReport/useWorklistReportSettings';
import {
  WorklistsReportDataRow,
  WorklistsReportPayloadEntry,
} from '@/app/components/Reporting/Collaboration/WorklistsReport/types';
import { mapReportDataForGrid } from '@/app/components/Reporting/Collaboration/WorklistsReport/data';
import { GridDataStateChangeEvent } from '@progress/kendo-react-grid';
import { useFormattedWorklistData } from '@/app/components/Reporting/Collaboration/WorklistsReport/useFormattedWorklistData';
import { useWorklistTemplates } from '@/app/components/Reporting/Collaboration/WorklistsReport/useWorklistTemplates';
import { useWorklistReportExcelAndGridData } from '@/app/components/Reporting/Collaboration/WorklistsReport/useWorklistReportExcelAndGridData';
import { getConfiguredText } from '@/app/components/ConfigurableLabels/UiLabel/UiLabel';

const getMyUnitOptions = () => {
  const orgUnitData = getOrgUnitData();
  const myUnits = getMyTeams().map(unit => unit.value);

  return myUnits.reduce((acc, uId): CommonWorkflowReportUnitData[] => {
    const unitId = parseInt(uId, 10);
    const foundUnit = orgUnitData.find(oud => oud.fOrgUnitId === unitId);

    if (!!foundUnit) {
      acc.push({
        unit: foundUnit.fOrgUnitName,
        unitNo: foundUnit.fOrgUnitNumber,
        unitId: foundUnit.fOrgUnitId,
      });
      return acc;
    }
    return acc;
  }, []);
};

export const useWorklistsReport = () => {
  const { reportSettings, setStates } = useWorklistReportSettings();
  const {
    teamCheck,
    subUnitCheck,
    selectedUnitData: toolbarSelectedUnit,
  } = reportSettings.current;
  const {
    setTeamCheck,
    setSubUnitCheck,
    setSelectedUnit: setToolbarSelectedUnit,
    setDataState,
  } = setStates;

  const templates = useWorklistTemplates();

  const reportName = getConfiguredText('laChecklistsAndTemplates');

  const toolbarUnitOptions: CommonWorkflowReportUnitData[] = useMemo(() => {
    return getMyUnitOptions();
  }, []);

  // States
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [reportDataRows, setReportDataRows] = useState<
    WorklistsReportDataRow[]
  >([]);

  const [isLoadingToolbarOptions, setIsLoadingToolbarOptions] = useState(true);

  // Roles
  const { managerType, isUserManager, isUserHR } = useMemo(() => {
    const getRoles = getLoginData();
    const loginRoles = getRoles.fLoginRoles;
    const mgrType = isOnlyAltMgr() ? 'amgr' : 'manager';

    return {
      managerType: mgrType,
      isUserManager: hasManagerRole(loginRoles),
      isUserHR: hasAnyHrRelatedRole(loginRoles),
    };
  }, []);

  // API & Request utils
  const ReportAPI = useMemo(() => {
    const userId = getLoggedUserId();
    const wlAPI = API.worklistReport;

    return {
      getByManagerId: () => wlAPI.getByManagerId(managerType, userId),
      getByManagerAllId: () => wlAPI.getByManagerAllId(managerType, userId),
      getByHrId: () => wlAPI.getByHrId(userId),
      getByHRDirectId: () => wlAPI.getByHRDirectId(userId),
      getBySubUnitId: (unitId: number) => wlAPI.getBySubUnitId(unitId),
      getByUnitId: (unitId: number) => wlAPI.getByUnitId(unitId),
    };
  }, [managerType]);

  const getReportUrlByConfig = useCallback(
    (updateProps: CommonWorkflowReportScopeSettings): string => {
      const isOnlyManager = isUserManager && !isUserHR;
      const isOnlyHR = isUserHR && !isUserManager;

      if (!!updateProps.selectedUnit) {
        if (updateProps.subUnitCheck) {
          return ReportAPI.getBySubUnitId(updateProps.selectedUnit.unitId);
        } else {
          return ReportAPI.getByUnitId(updateProps.selectedUnit.unitId);
        }
      } else {
        if (updateProps.teamCheck) {
          return isOnlyHR
            ? ReportAPI.getByHRDirectId()
            : ReportAPI.getByManagerId();
        }
        if (updateProps.subUnitCheck) {
          return isOnlyManager
            ? ReportAPI.getByManagerAllId()
            : ReportAPI.getByHrId();
        } else {
          return isOnlyHR
              ? ReportAPI.getByHRDirectId()
              : ReportAPI.getByManagerId();
        }
      }
    },
    [ReportAPI, isUserManager, isUserHR],
  );

  const getValidReportToolbarSettings = useCallback(
    (
      settings: ReportToolbarSettingsChange,
    ): CommonWorkflowReportScopeSettings => {
      const { action, ...selections } = settings;

      switch (action) {
        case ReportToolbarSettingsChangeAction.SelectedUnit:
          return {
            teamCheck: false,
            subUnitCheck: !selections.selectedUnit,
            selectedUnit: !!selections.selectedUnit
              ? selections.selectedUnit
              : undefined,
          };
        case ReportToolbarSettingsChangeAction.TeamCheck:
          return {
            ...selections,
            subUnitCheck: !selections.selectedUnit && !selections.teamCheck,
            selectedUnit: undefined,
          };
        case ReportToolbarSettingsChangeAction.SubUnitCheck:
          return {
            ...selections,
            teamCheck: !selections.selectedUnit && !selections.subUnitCheck,
          };
        case ReportToolbarSettingsChangeAction.LoadSavedSettings:
          if (!!selections.selectedUnit) {
            return {
              ...selections,
              teamCheck: false,
            };
          } else {
            return {
              ...selections,
              subUnitCheck: !selections.teamCheck,
            };
          }
        default:
          return {
            teamCheck: reportSettings.initial.teamCheck,
            subUnitCheck: reportSettings.initial.subUnitCheck,
            selectedUnit: reportSettings.initial.selectedUnitData,
          };
      }
    },
    [],
  );

  // Data updating & API utils
  const updateWorklistReportData = useCallback(
    (
      requestUrl?: string,
      setData?: (data: WorklistsReportDataRow[]) => void,
    ) => {
      if (!!requestUrl) {
        Service.get(
          requestUrl,
          (res: WorklistsReportPayloadEntry[]) => {
            setData(mapReportDataForGrid(res));
            setLoading(false);
            setError('');
          },
          (err: any) => {
            setData([]);
            setLoading(false);
            setError(err);
          },
        );
      }
    },
    [mapReportDataForGrid, setLoading, setError],
  );

  const updateReportBySettings = useCallback(
    (settings: CommonWorkflowReportScopeSettings) => {
      setTeamCheck(settings.teamCheck);
      setSubUnitCheck(settings.subUnitCheck);
      setToolbarSelectedUnit(settings.selectedUnit);

      updateWorklistReportData(
        getReportUrlByConfig(settings),
        setReportDataRows,
      );
    },
    [
      setTeamCheck,
      setSubUnitCheck,
      setToolbarSelectedUnit,
      updateWorklistReportData,
      getReportUrlByConfig,
      setReportDataRows,
    ],
  );

  const getUnitDataFromReportDataRow = useCallback(
    (dataRow: WorklistsReportDataRow): CommonWorkflowReportUnitData => {
      const { unit, unitId, unitNo } = dataRow;
      return { unit, unitId, unitNo };
    },
    [],
  );

  // Initial data fetching
  useEffect(() => {
    if (isLoadingToolbarOptions) {
      const initial = reportSettings.initial;

      new Promise<WorklistsReportDataRow[]>(resolve =>
        updateWorklistReportData(
          getReportUrlByConfig({
            teamCheck: initial.teamCheck,
            subUnitCheck: initial.subUnitCheck,
            selectedUnit: initial.selectedUnitData,
          }),
          resolve,
        ),
      ).then(data => {
        if (data.length) {
          setReportDataRows(data);
        }
        setIsLoadingToolbarOptions(false);
      });
    }
  }, [
    updateWorklistReportData,
    setReportDataRows,
    getUnitDataFromReportDataRow,
    setIsLoadingToolbarOptions,
    isUserManager,
    isUserHR,
  ]);

  const onReportToolbarSettingsChange = useCallback(
    (settings: ReportToolbarSettingsChange) => {
      updateReportBySettings(getValidReportToolbarSettings(settings));
    },
    [updateReportBySettings, getValidReportToolbarSettings],
  );

  const toolbarSettings: WorkflowsReportToolbarSettings = useMemo(() => {
    return {
      unitOptions: toolbarUnitOptions,
      selectedUnit: toolbarSelectedUnit,
      teamCheck,
      subUnitCheck,
      enabledActions: {
        unitSelector: true,
        teamCheckbox: isUserManager || isUserHR,
        subUnitsCheckbox: isUserManager || isUserHR,
      },
      onReportToolbarSettingsChange,
    };
  }, [
    toolbarUnitOptions,
    toolbarSelectedUnit,
    teamCheck,
    subUnitCheck,
    onReportToolbarSettingsChange,
    isUserManager,
    isUserHR,
  ]);

  const onDataStateChange = useCallback(
    (event: GridDataStateChangeEvent): void => {
      setDataState({
        filter: event.data.filter,
        skip: event.data.skip,
        take: event.data.take,
        sort: event.data.sort,
      });
    },
    [],
  );

  const formattedReportDataRows = useFormattedWorklistData(
    reportDataRows,
    templates.collaborationTemplates,
  );

  const { worklistsData, filterCellOptions } = useMemo(() => {
    // custom filter for workflow unit column
    const uOps = Array.from(
      new Set(formattedReportDataRows.map(field => field.unitNo)),
    );

    return {
      worklistsData: formattedReportDataRows,
      filterCellOptions: {
        unitOptions: uOps,
      },
    };
  }, [formattedReportDataRows]);

  const { gridData, excelData } = useWorklistReportExcelAndGridData({
    reportName,
    worklistsData,
  });

  return {
    reportName,
    gridData,
    excelData,
    loading: loading || isLoadingToolbarOptions || templates.loading,
    onColumnsSubmit: setStates.setColumns,
    filterCellOptions,
    toolbarSettings,
    reportSettings,
    onDataStateChange,
    error: error || templates.error,
  };
};
